import type { ClassifiedCategory, ClassifiedSellerType } from '../constants/classified';

/** Minimal classified fields for JSON-LD (client + prerender). */
export type ClassifiedSchemaInput = {
  id: string;
  title: string;
  description?: string;
  priceCents: number;
  currency: string;
  images?: string[];
  parish: string;
  category: ClassifiedCategory;
  slug?: string;
  areaLabel?: string;
  latitude?: number;
  longitude?: number;
  bedrooms?: number;
  bathrooms?: number;
  sqftInterior?: number;
  sqftLot?: number;
  yearBuilt?: number;
  sellerType?: ClassifiedSellerType;
  listingAgentName?: string;
  contactPhone?: string;
  createdAt?: string;
  updatedAt?: string;
};

export type PropertyListingSchemaInput = {
  title: string;
  description?: string;
  url: string;
  images?: string[];
  parish: string;
  community?: string;
  addressDisplay?: string;
  lat?: number | null;
  lng?: number | null;
  price?: number;
  priceCurrency?: string;
  pricePeriod?: 'once' | 'monthly' | 'weekly';
  status?: 'for_sale' | 'for_rent' | 'sold' | 'rented';
  bedrooms?: number | null;
  bathrooms?: number | null;
  areaSqft?: number | null;
  lotAcres?: number | null;
  agent?: {
    name: string;
    company?: string;
    phoneDisplay?: string;
    email?: string;
  };
};

function absoluteImage(baseUrl: string, image: string): string {
  if (image.startsWith('http://') || image.startsWith('https://')) return image;
  const base = baseUrl.replace(/\/$/, '');
  return `${base}${image.startsWith('/') ? image : `/${image}`}`;
}

function stripHtml(text: string): string {
  return text.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

function offerFromPrice(
  pageUrl: string,
  priceCents: number,
  currency: string,
  availability = 'https://schema.org/InStock',
): Record<string, unknown> {
  return {
    '@type': 'Offer',
    url: pageUrl,
    price: (priceCents / 100).toFixed(2),
    priceCurrency: currency || 'XCD',
    availability,
  };
}

function postalAddress(parish: string, locality?: string, street?: string) {
  const address: Record<string, unknown> = {
    '@type': 'PostalAddress',
    addressRegion: parish,
    addressCountry: 'GD',
  };
  if (locality?.trim()) address.addressLocality = locality.trim();
  if (street?.trim()) address.streetAddress = street.trim();
  return address;
}

function geoCoordinates(lat?: number | null, lng?: number | null) {
  if (lat == null || lng == null) return undefined;
  return { '@type': 'GeoCoordinates', latitude: lat, longitude: lng };
}

function hiringOrganization(
  sellerType: ClassifiedSellerType | undefined,
  title: string,
  pageUrl: string,
): Record<string, unknown> {
  if (sellerType === 'business') {
    return {
      '@type': 'Organization',
      name: title.split('—')[0]?.trim() || 'Employer on ShopGND',
      sameAs: pageUrl,
    };
  }
  return {
    '@type': 'Organization',
    name: 'ShopGND Classifieds',
    sameAs: 'https://www.shopgnd.gd/grenada/classifieds/',
  };
}

/** JobPosting for jobs category classifieds. */
export function buildJobPostingSchema(
  c: ClassifiedSchemaInput,
  pageUrl: string,
): Record<string, unknown> {
  const description = stripHtml(c.description ?? c.title);
  const posted = c.createdAt ?? c.updatedAt ?? new Date().toISOString();
  const validThrough = new Date(posted);
  validThrough.setDate(validThrough.getDate() + 60);

  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title: c.title,
    description,
    datePosted: posted.split('T')[0],
    validThrough: validThrough.toISOString().split('T')[0],
    url: pageUrl,
    hiringOrganization: hiringOrganization(c.sellerType, c.title, pageUrl),
    jobLocation: {
      '@type': 'Place',
      address: postalAddress(c.parish, c.areaLabel),
    },
    identifier: {
      '@type': 'PropertyValue',
      name: 'ShopGND listing id',
      value: c.id,
    },
  };

  if (c.priceCents > 0) {
    schema.baseSalary = {
      '@type': 'MonetaryAmount',
      currency: c.currency || 'XCD',
      value: {
        '@type': 'QuantitativeValue',
        value: (c.priceCents / 100).toFixed(2),
        unitText: 'MONTH',
      },
    };
  }

  return schema;
}

/** Car schema for vehicle classifieds (schema.org/Car). */
export function buildVehicleSchema(
  c: ClassifiedSchemaInput,
  pageUrl: string,
  baseUrl: string,
): Record<string, unknown> {
  const images = c.images?.filter(Boolean) ?? [];
  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Car',
    name: c.title,
    description: stripHtml(c.description ?? `${c.title} for sale in ${c.parish}, Grenada.`),
    url: pageUrl,
  };

  if (images.length) {
    schema.image = images.map((img) => absoluteImage(baseUrl, img));
  }

  if (c.yearBuilt) {
    schema.vehicleModelDate = String(c.yearBuilt);
  }

  if (c.priceCents >= 0) {
    schema.offers = offerFromPrice(pageUrl, c.priceCents, c.currency);
  }

  schema.itemLocation = {
    '@type': 'Place',
    address: postalAddress(c.parish, c.areaLabel),
  };

  return schema;
}

/** RealEstateListing from classified property category. */
export function buildClassifiedPropertySchema(
  c: ClassifiedSchemaInput,
  pageUrl: string,
  baseUrl: string,
): Record<string, unknown> {
  const images = c.images?.filter(Boolean) ?? [];
  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'RealEstateListing',
    name: c.title,
    description: stripHtml(c.description ?? c.title),
    url: pageUrl,
    address: postalAddress(c.parish, c.areaLabel),
  };

  if (images.length) schema.image = images.map((img) => absoluteImage(baseUrl, img));

  const geo = geoCoordinates(c.latitude, c.longitude);
  if (geo) schema.geo = geo;

  if (c.bedrooms != null) schema.numberOfBedrooms = c.bedrooms;
  if (c.bathrooms != null) schema.numberOfBathroomsTotal = c.bathrooms;
  if (c.sqftInterior) {
    schema.floorSize = { '@type': 'QuantitativeValue', value: c.sqftInterior, unitCode: 'FTK' };
  }
  if (c.sqftLot) {
    schema.lotSize = { '@type': 'QuantitativeValue', value: c.sqftLot, unitCode: 'FTK' };
  }

  if (c.priceCents >= 0) {
    schema.offers = offerFromPrice(pageUrl, c.priceCents, c.currency);
  }

  if (c.listingAgentName?.trim()) {
    schema.realEstateAgent = {
      '@type': 'RealEstateAgent',
      name: c.listingAgentName.trim(),
      ...(c.contactPhone?.trim() ? { telephone: c.contactPhone.trim() } : {}),
    };
  }

  return schema;
}

/** Generic Product fallback for other classified categories. */
export function buildClassifiedProductSchema(
  c: ClassifiedSchemaInput,
  pageUrl: string,
  baseUrl: string,
): Record<string, unknown> {
  const images = c.images?.filter(Boolean) ?? [];
  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: c.title,
    description: stripHtml(c.description ?? c.title),
    url: pageUrl,
  };
  if (images.length) schema.image = images.map((img) => absoluteImage(baseUrl, img));
  if (c.priceCents >= 0) {
    schema.offers = offerFromPrice(pageUrl, c.priceCents, c.currency);
  }
  return schema;
}

/** Pick JobPosting / Car / RealEstateListing / Product by classified category. */
export function buildClassifiedStructuredData(
  c: ClassifiedSchemaInput,
  options: { pageUrl: string; baseUrl?: string },
): Record<string, unknown> {
  const baseUrl = options.baseUrl?.replace(/\/$/, '') ?? 'https://www.shopgnd.gd';
  const pageUrl = options.pageUrl;

  if (c.category === 'jobs') return buildJobPostingSchema(c, pageUrl);
  if (c.category === 'vehicles') return buildVehicleSchema(c, pageUrl, baseUrl);
  if (c.category === 'property') return buildClassifiedPropertySchema(c, pageUrl, baseUrl);
  return buildClassifiedProductSchema(c, pageUrl, baseUrl);
}

function propertyOffer(
  pageUrl: string,
  input: PropertyListingSchemaInput,
): Record<string, unknown> | undefined {
  if (input.price == null || input.price < 0) return undefined;

  const currency = input.priceCurrency?.trim() || 'XCD';
  const offer: Record<string, unknown> = {
    '@type': 'Offer',
    url: pageUrl,
    price: input.price.toFixed(2),
    priceCurrency: currency,
    availability:
      input.status === 'sold' || input.status === 'rented'
        ? 'https://schema.org/SoldOut'
        : 'https://schema.org/InStock',
  };

  if (input.pricePeriod === 'monthly') {
    offer.priceSpecification = {
      '@type': 'UnitPriceSpecification',
      price: input.price.toFixed(2),
      priceCurrency: currency,
      unitText: 'MONTH',
    };
  } else if (input.pricePeriod === 'weekly') {
    offer.priceSpecification = {
      '@type': 'UnitPriceSpecification',
      price: input.price.toFixed(2),
      priceCurrency: currency,
      unitText: 'WEEK',
    };
  }

  return offer;
}

/** Rich RealEstateListing for agency PropertyListing records (client + prerender). */
export function buildPropertyListingStructuredData(
  input: PropertyListingSchemaInput,
): Record<string, unknown> {
  const images = input.images?.filter(Boolean) ?? [];
  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'RealEstateListing',
    name: input.title,
    description: stripHtml(input.description ?? input.title),
    url: input.url,
    address: postalAddress(input.parish, input.community, input.addressDisplay),
  };

  if (images.length) schema.image = images;

  const geo = geoCoordinates(input.lat, input.lng);
  if (geo) schema.geo = geo;

  if (input.bedrooms != null) schema.numberOfBedrooms = input.bedrooms;
  if (input.bathrooms != null) schema.numberOfBathroomsTotal = input.bathrooms;
  if (input.areaSqft) {
    schema.floorSize = { '@type': 'QuantitativeValue', value: input.areaSqft, unitCode: 'FTK' };
  }
  if (input.lotAcres) {
    schema.lotSize = {
      '@type': 'QuantitativeValue',
      value: input.lotAcres,
      unitCode: 'ACR',
    };
  }

  const offer = propertyOffer(input.url, input);
  if (offer) schema.offers = offer;

  if (input.agent?.name?.trim()) {
    schema.realEstateAgent = {
      '@type': 'RealEstateAgent',
      name: input.agent.name.trim(),
      ...(input.agent.company?.trim()
        ? { worksFor: { '@type': 'Organization', name: input.agent.company.trim() } }
        : {}),
      ...(input.agent.phoneDisplay?.trim() ? { telephone: input.agent.phoneDisplay.trim() } : {}),
      ...(input.agent.email?.trim() ? { email: input.agent.email.trim() } : {}),
    };
  }

  return schema;
}
